// «Utsett varsel» øverst på en åpen ordre (OrderDetails). Er varen forsinket (f.eks. restordre),
// kan den ansatte velge en dato, og fram til da teller ordren ikke som gammel: ingen trekant på
// kontrollpanelet eller /ordre, og ikke med i varselet ved innlogging. Standard er
// UTSETT_STANDARD_VIRKEDAGER virkedager fra i dag (helligdager tatt med).
import React, { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import Ikon from '../ny-kundesamhandling/Ikon';
import { feltKlasse, knappPrimar, knappSekundar } from '../ny-kundesamhandling/Skjema';
import { GAMMEL_ETTER, UTSETT_STANDARD_VIRKEDAGER, standardUtsettDato, datoOmDager, visDato, virkedagerTekst } from '../utils/mineOrdrer';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const lenkeKnapp =
  'inline-flex min-h-[2.25rem] shrink-0 items-center gap-2 rounded-xl border-2 bg-white px-3 py-1.5 font-semibold focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50';

export default function UtsettOrdreVarsel({ ordre, onEndret }) {
  const [redigerer, setRedigerer] = useState(false);
  const [til, setTil] = useState('');
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState('');
  const [melding, setMelding] = useState('');
  const datoRef = useRef(null);

  // Fokus til datofeltet når skjemaet åpnes.
  useEffect(() => {
    if (redigerer) datoRef.current?.focus();
  }, [redigerer]);

  const utsatt = ordre.utsatt && ordre.varselUtsatt?.til;
  const gammel = !ordre.utsatt && (ordre.virkedager ?? 0) > GAMMEL_ETTER;
  const iMorgen = datoOmDager(1);
  const sisteDato = datoOmDager(366);

  const apne = () => {
    setTil(utsatt ? ordre.varselUtsatt.til : standardUtsettDato());
    setFeil('');
    setMelding('');
    setRedigerer(true);
  };

  const send = async (metode, body) => {
    setLagrer(true);
    setFeil('');
    try {
      const r = await fetch(`${API_BASE_URL}/orders/${ordre._id}/varsel-utsatt`, {
        method: metode,
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || 'Kunne ikke lagre');
      onEndret(data);
      setRedigerer(false);
      return data;
    } catch (e) {
      setFeil(e.message || 'Kunne ikke lagre. Prøv igjen.');
      return null;
    } finally {
      setLagrer(false);
    }
  };

  const lagre = async (e) => {
    e.preventDefault();
    if (!til || til < iMorgen || til > sisteDato) {
      setFeil('Velg en dato fra i morgen og inntil ett år fram.');
      return;
    }
    const data = await send('PUT', { til, ansatt: Cookies.get('selectedEmployee') || '' });
    if (data) setMelding(`Varselet er utsatt til ${visDato(til)}.`);
  };

  const fjern = async () => {
    const data = await send('DELETE');
    if (data) setMelding('Utsettelsen er fjernet.');
  };

  const ramme = utsatt
    ? 'border-sky-200 bg-sky-50'
    : gammel
    ? 'border-red-200 bg-red-50'
    : 'border-slate-200 bg-slate-50';

  return (
    <section aria-labelledby="uv-tittel" className={`rounded-2xl border px-3 py-1.5 ${ramme}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white ${utsatt ? 'bg-sky-700' : gammel ? 'bg-red-700' : 'bg-slate-600'}`}>
          <Ikon navn={utsatt ? 'kalenderKlokke' : 'varsel'} className="w-5 h-5" />
        </span>
        <div className="min-w-0 flex-1 basis-56">
          <h2 id="uv-tittel" className="font-bold text-slate-900">
            {utsatt
              ? `Varselet er utsatt til ${visDato(ordre.varselUtsatt.til)}`
              : gammel
              ? `Åpen i ${virkedagerTekst(ordre.virkedager)}`
              : 'Varsel om gammel ordre'}
          </h2>
          <p className="text-sm text-slate-700">
            {utsatt
              ? `Satt${ordre.varselUtsatt.av ? ` av ${ordre.varselUtsatt.av}` : ''}. Ordren regnes ikke som gammel før da.`
              : gammel
              ? 'Er varen forsinket? Utsett varselet, så ordren ikke regnes som gammel før den kan komme.'
              : `Ordren varsles etter ${GAMMEL_ETTER} virkedager. Vet du at varen blir forsinket, kan du utsette varselet.`}
          </p>
        </div>
        {!redigerer && (
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={apne} className={`${lenkeKnapp} border-slate-300 text-slate-900 hover:bg-slate-100`}>
              <Ikon navn={utsatt ? 'blyant' : 'kalenderKlokke'} className="w-5 h-5" />
              {utsatt ? 'Endre dato' : 'Utsett varsel'}
            </button>
            {utsatt && (
              <button type="button" onClick={fjern} disabled={lagrer} className={`${lenkeKnapp} border-slate-300 text-slate-900 hover:bg-slate-100 disabled:opacity-60`}>
                Fjern
              </button>
            )}
          </div>
        )}
      </div>

      {redigerer && (
        <form onSubmit={lagre} noValidate className="mt-3 flex flex-wrap items-end gap-2 border-t border-slate-200 pt-3">
          <div className="min-w-0 basis-56">
            <label htmlFor="uv-dato" className="mb-1 block font-semibold text-slate-800">Varsle igjen fra</label>
            <input
              id="uv-dato"
              type="date"
              value={til}
              min={iMorgen}
              max={sisteDato}
              onChange={(e) => setTil(e.target.value)}
              aria-describedby="uv-hjelp"
              ref={datoRef}
              className={feltKlasse}
            />
            <p id="uv-hjelp" className="mt-1 text-sm text-slate-600">Standard er {UTSETT_STANDARD_VIRKEDAGER} virkedager fra i dag.</p>
          </div>
          <div className="flex flex-wrap gap-2 pb-6">
            <button type="submit" disabled={lagrer} className={knappPrimar}>{lagrer ? 'Lagrer …' : 'Utsett'}</button>
            <button type="button" onClick={() => setRedigerer(false)} className={knappSekundar}>Avbryt</button>
          </div>
        </form>
      )}

      {feil && <p role="alert" className="mt-2 text-red-800">{feil}</p>}
      <p role="status" className="sr-only">{melding}</p>
    </section>
  );
}
